import { useEffect } from 'react';
import { useBillingStore } from './useBillingStore';
import type { BillingClientFactory } from './types';

interface SyncOptions {
  enabled: boolean;
  userId: string | null | undefined;
  clientFactory: BillingClientFactory;
}

// Ref-count active mounts so the factory only clears when the last consumer
// unmounts. Without this, two sibling mounts (page + drawer) racing through
// React StrictMode or hot-reload would leave the store with `clientFactory:
// null` while the surviving consumer still depends on it, breaking any
// in-flight or subsequent `fetch` calls.
let mountCount = 0;

/**
 * Wires the auth-aware client factory into the billing store and triggers
 * initial fetches when the user becomes available. Safe to mount in multiple
 * places (e.g. drawer + table); ref-counting keeps the factory pinned until
 * the last mount unwinds.
 */
export function useBillingSync({ enabled, userId, clientFactory }: SyncOptions): void {
  const setClientFactory = useBillingStore((s) => s.setClientFactory);
  const fetchBilling = useBillingStore((s) => s.fetch);
  const fetchCatalog = useBillingStore((s) => s.fetchCatalog);
  const resetBilling = useBillingStore((s) => s.reset);

  useEffect(() => {
    mountCount += 1;
    setClientFactory(clientFactory);
    return () => {
      mountCount -= 1;
      if (mountCount === 0) setClientFactory(null);
    };
  }, [clientFactory, setClientFactory]);

  useEffect(() => {
    if (!enabled || !userId) {
      resetBilling();
      return;
    }
    void fetchBilling(userId);
    void fetchCatalog(userId);
  }, [enabled, userId, fetchBilling, fetchCatalog, resetBilling]);
}
